import styled from "styled-components";
import type { GlobalToken } from "antd";

export interface TokenProps {
  $token: GlobalToken;
}

export const SectionLabel = styled.div<TokenProps>`
  font-size: ${({ $token }) => $token.fontSize}px;
  font-weight: 700;
  letter-spacing: -0.2px;
  color: ${({ $token }) => $token.colorText};
  padding: 14px 8px 8px;

  /* The first label ("Settings") sits at the top of the card — trim its lead. */
  &:first-child {
    padding-top: 2px;
  }
`;

// Settings row (General / Documents / …): icon + name, no number/handle/remove.
export const Row = styled.button<TokenProps & { $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid
    ${({ $token, $active }) => ($active ? $token.colorBorder : "transparent")};
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  font-size: ${({ $token }) => $token.fontSize}px;
  font-weight: 500;
  color: ${({ $token, $active }) =>
    $active ? $token.colorText : $token.colorTextSecondary};
  background: ${({ $token, $active }) =>
    $active ? $token.colorFillSecondary : "transparent"};
  transition: background ${({ $token }) => $token.motionDurationFast};

  &:hover {
    background: ${({ $token, $active }) =>
      $active ? $token.colorFillSecondary : $token.colorFillQuaternary};
  }
`;

export const DragHandle = styled.button<TokenProps>`
  position: absolute;
  left: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ $token }) => $token.colorTextQuaternary};
  cursor: grab;
  opacity: 0;
  transition: opacity ${({ $token }) => $token.motionDurationFast};

  &:focus-visible {
    opacity: 1;
    outline: 2px solid ${({ $token }) => $token.colorPrimaryBorder};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

// Add-on row: number + icon + name + remove, with a hover shift that reveals
// the drag handle on the left. A passive container div — the handle, the
// select action and the remove action are each their own <button> inside.
export const AddonRow = styled.div<
  TokenProps & { $active: boolean; $dragging: boolean }
>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid
    ${({ $token, $active }) => ($active ? $token.colorBorder : "transparent")};
  border-radius: 10px;
  text-align: left;
  font-family: inherit;
  font-size: ${({ $token }) => $token.fontSize}px;
  font-weight: 500;
  color: ${({ $token, $active }) =>
    $active ? $token.colorText : $token.colorTextSecondary};
  background: ${({ $token, $active, $dragging }) =>
    $dragging
      ? $token.colorBgElevated
      : $active
        ? $token.colorFillSecondary
        : "transparent"};
  box-shadow: ${({ $token, $dragging }) =>
    $dragging ? $token.boxShadowSecondary : "none"};
  transition: padding-left ${({ $token }) => $token.motionDurationFast},
    background ${({ $token }) => $token.motionDurationFast};

  &:hover {
    padding-left: 24px;
    background: ${({ $token, $active }) =>
      $active ? $token.colorFillSecondary : $token.colorFillQuaternary};
  }

  &:hover ${DragHandle},
  &:focus-within ${DragHandle} {
    opacity: 1;
  }

  &:focus-within {
    padding-left: 24px;
  }
`;

export const OrderNum = styled.span<TokenProps>`
  width: 16px;
  flex-shrink: 0;
  text-align: center;
  font-size: ${({ $token }) => $token.fontSizeSM}px;
  color: ${({ $token }) => $token.colorTextTertiary};
`;

// The clickable "select this add-on" area (icon + name). A real button so it's
// keyboard-focusable without making the whole row a button.
export const SelectButton = styled.button`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
`;

export const ItemName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// "Add a new add-on" now renders via the shared `DashedAddButton` component
// (see ./DashedAddButton.tsx), which owns this styling — kept there so the
// Documents card's "Add a document" button can reuse the exact same CSS.
